import { useState, useEffect, useRef } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ECMLayout from './ECMLayout';
import { applyTheme, getSelectedTheme } from '../themes';
import { isAuthenticated, msalInstance, logout, getCurrentUser } from '../auth/LoginButton';
import { getUserRoles, getHomeRoute, isDripOnlyUser, dripOnlyRedirect } from '../rbac/roles';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_BEFORE_MS = 60 * 1000;

export default function ECMRoutesWrapper() {
  const [currentTheme, setCurrentTheme] = useState(getSelectedTheme());
  const [msalReady, setMsalReady] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        console.log('✅ [ECM] MSAL initialized');
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          console.log('✅ [ECM] Redirect handled:', response.account?.username);
        }
        setMsalReady(true);
      } catch (error) {
        console.error('❌ [ECM] MSAL initialization failed:', error);
        setMsalReady(true);
      }
    };
    initializeMsal();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/SIMS', { replace: true });
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    setShowIdleWarning(false);
    warningTimerRef.current = setTimeout(() => setShowIdleWarning(true), IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
    idleTimerRef.current = setTimeout(() => handleLogout(), IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, []);

  const handleThemeChange = (themeName: string) => {
    setCurrentTheme(themeName);
  };

  if (!isAuthenticated()) {
    return <Navigate to="/SIMS" replace />;
  }

  // ECM access = SIMS Admin + Campaign Manager (full dashboard) + Graduate.Admin/Graduate.Viewer
  // (Graduate Dashboard tab only — ECMDashboard hides the other tabs for them). Survey.Agent,
  // Survey.Creator, Survey.Viewer, etc. are excluded. Defense-in-depth behind the RoleRoute that
  // wraps this route in App.tsx: re-checked here on every render so the layout never mounts for a
  // disallowed role even if this wrapper is ever reached without RoleRoute.
  const _u: any = getCurrentUser();
  // MUST stay in step with the allow-list on the /ECM route in App.tsx. A role admitted there but
  // missing here does not get an error - it gets sent to its own home route, which for a role whose
  // home IS an /ECM page means arriving back at this same check. That is a silent redirect loop and
  // a blank screen, which is exactly how Drip.Manager failed: nothing thrown, nothing logged, no
  // clue where to look.
  const ECM_ACCESS_ROLES = ['Survey.Admin', 'Campaign.Manager', 'Drip.Manager',
                            'Graduate.Admin', 'Graduate.Viewer'];
  if (!getUserRoles(_u).some((r: string) => ECM_ACCESS_ROLES.includes(r))) {
    const _home = getHomeRoute(_u);
    // Never redirect to the page we are already on. Without this the mismatch above renders as a
    // blank screen; with it, the user is told what is wrong and by whom it can be fixed.
    if (_home === location.pathname) {
      console.error('[ECM] Role has no ECM access but its home route is this page:',
                    getUserRoles(_u), _home);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center',
                   alignItems: 'center', minHeight: '100vh', background: 'var(--bg-primary)',
                   color: 'var(--text-primary)', textAlign: 'center', p: 4 }}>
          <h2 style={{ margin: 0 }}>This account cannot open the dashboard</h2>
          <p style={{ maxWidth: 520, color: 'var(--text-secondary)' }}>
            Your role is recognised at sign-in but is not permitted here, so there is nowhere to
            send you. Please pass this to IT: the role{' '}
            <b>{getUserRoles(_u).join(', ') || '(none)'}</b> needs adding to the ECM access list.
          </p>
        </Box>
      );
    }
    return <Navigate to={_home} replace />;
  }

  // Graduate.Admin / Graduate.Viewer (without a full-ECM role) may use ONLY the Graduate Dashboard.
  // Enforce at the LAYOUT level so it covers EVERY /ECM subroute (new-campaign, job/:id, etc.),
  // not just the ECMDashboard component — otherwise a graduate-only user could type
  // /ECM/new-campaign or /ECM/job/:id and reach campaign screens outside their scope.
  const _isGraduateOnly =
    getUserRoles(_u).some((r: string) => ['Graduate.Admin', 'Graduate.Viewer'].includes(r)) &&
    !getUserRoles(_u).some((r: string) => ['Survey.Admin', 'Campaign.Manager'].includes(r));
  if (_isGraduateOnly && !location.pathname.endsWith('/graduates')) {
    return <Navigate to="/ECM/Dashboard/graduates" replace />;
  }
  // The same layout-level seal for a Drip Campaign Manager without a broader role. ECMDashboard's
  // own redirect never runs on /ECM/new-campaign or /ECM/job/:id, so without this a Drip.Manager
  // could type /ECM/new-campaign and get New Campaign, whose SendGrid picker lists the campaign
  // templates they must not see.
  const _dripOnlyTo = isDripOnlyUser(getUserRoles(_u)) ? dripOnlyRedirect(location.pathname) : null;
  if (_dripOnlyTo) {
    return <Navigate to={_dripOnlyTo} replace />;
  }

  if (!msalReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--bg-primary)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ECMLayout currentTheme={currentTheme} onThemeChange={handleThemeChange}>
      {showIdleWarning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: 'var(--card-bg, #1e2a3a)',
            border: '1px solid var(--card-border, #2d3f55)',
            borderRadius: '12px',
            padding: '36px 40px',
            maxWidth: '420px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏱️</div>
            <h2 style={{ margin: '0 0 10px', color: 'var(--text-primary, #fff)', fontSize: '20px' }}>
              Session Expiring Soon
            </h2>
            <p style={{ margin: '0 0 24px', color: 'var(--text-secondary, #aaa)', fontSize: '14px', lineHeight: 1.6 }}>
              You've been inactive for 14 minutes. You'll be logged out in <strong>1 minute</strong> unless you continue.
            </p>
            <button
              onClick={resetIdleTimer}
              style={{
                padding: '11px 32px',
                background: 'var(--accent, #2196f3)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginRight: '12px'
              }}
            >
              Stay Logged In
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '11px 24px',
                background: 'transparent',
                color: 'var(--text-secondary, #aaa)',
                border: '1px solid var(--card-border, #2d3f55)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Logout Now
            </button>
          </div>
        </div>
      )}
      <Outlet />
    </ECMLayout>
  );
}
